import { 
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, Chip, 
    useTheme, useMediaQuery, Fade, Slide, CardContent
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";
import PaymentApi from "Service/Payment.api";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { StyledTableCell, HeaderCard, StyledContainer, StyledTableRow } from "./ProfessionalUserList";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';

const RewardRedeemRequests = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [requests, setRequests] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchPendingRequests();
    }, []);

    const fetchPendingRequests = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Fetching requests..." }));
        try {
            const response = await PaymentApi.getAllRedeemRequests();
            if (response.success) setRequests(response?.data.data || []);
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    const handleAction = async (requestId: number, action: 'Approve' | 'Reject') => {
        dispatch(startLoading({ loading: true, loadtext: `${action}ing request...` }));
        try {
            const response = await PaymentApi.verifyRedemption(requestId);
            if (response.success) {
                fetchPendingRequests();
            }
        } catch (error) {
            console.error("Action error:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box sx={{ width: '100%', pb: 10 }}>
                    {/* Back Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Back
                        </Button>
                    </Box>

                    {/* Header Section */}
                    <HeaderCard elevation={8} sx={{ mb: 4 }}>
                        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexDirection={{ xs: 'column', md: 'row' }}
                                gap={2}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <CurrencyExchangeIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                                    <Box>
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            fontWeight="bold"
                                            color="white"
                                            gutterBottom
                                        >
                                            Redemption Approval
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ opacity: 0.9 }}
                                            color="white"
                                        >
                                            Review and process reward point redemption requests from clients
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </HeaderCard>

                    <Slide direction="up" in timeout={600}>
                        <TableContainer 
                            component={Paper} 
                            sx={{ 
                                borderRadius: 3, 
                                boxShadow: 4,
                                overflow: 'hidden'
                            }}
                        >
                            <Table stickyHeader sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>User ID</StyledTableCell>
                                        <StyledTableCell>User Name</StyledTableCell>
                                        <StyledTableCell>Requested Points</StyledTableCell>
                                        <StyledTableCell>Request Date</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {requests?.length ? (
                                        requests?.map((req: any) => (
                                            <StyledTableRow key={req.id} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>{req.clientId}</TableCell>
                                                <TableCell>{req.userName}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#283165' }}>
                                                    {req.pointsRequested} pts
                                                </TableCell>
                                                <TableCell>{req.date}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={req.status} 
                                                        color={req.status === "Approved" ? "success" : req.status === "Pending" ? "warning" : "error"} 
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {req.status === "Pending" ? (
                                                        <Box display="flex" gap={1} justifyContent="center">
                                                            <Button 
                                                                variant="contained" 
                                                                color="success" 
                                                                size="small"
                                                                startIcon={<CheckCircleIcon />}
                                                                onClick={() => handleAction(req.id, 'Approve')}
                                                                sx={{ borderRadius: 2 }}
                                                            >
                                                                Accept
                                                            </Button>
                                                            <Button 
                                                                variant="outlined" 
                                                                color="error" 
                                                                size="small"
                                                                startIcon={<CancelIcon />}
                                                                onClick={() => handleAction(req.id, 'Reject')}
                                                                sx={{ borderRadius: 2 }}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Box>
                                                    ) : (
                                                        <Chip label="Processed" size="small" variant="filled" sx={{ opacity: 0.7 }} />
                                                    )}
                                                </TableCell>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                                <Typography variant="body1" color="textSecondary">
                                                    No pending redemption requests found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Slide>
                </Box>
            </Fade>
        </StyledContainer>
    );
};

export default RewardRedeemRequests;
